import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import {
	FormControl,
	FormControlLabel,
	FormLabel,
	Grid,
	Radio,
	RadioGroup
} from '@mui/material';
import React, { FC } from 'react';
import {
	CONNECTORS_FORM_KEYS,
	CONNECTORS_FORM_KEYS as FORM_KEYS,
	IConnectorEditDialog
} from 'types/connectors';

const locale = {
	serviceName: `connectors.edit.dialog.form.field.serviceName`,
	authToken: `connectors.edit.dialog.form.field.authToken`,
	apiKey: `connectors.edit.dialog.form.field.apiKey`,
	apiSecret: 'connectors.edit.dialog.form.field.apiSecret',
	fromEmail: `connectors.edit.dialog.form.field.fromEmail`,
	fromPhone: `connectors.edit.dialog.form.field.fromPhone`,
	number: `connectors.edit.dialog.form.field.number`,
	accountSid: `connectors.edit.dialog.form.field.accountSid`,
	messagingServiceSid: `connectors.edit.dialog.form.field.messagingServiceSid`
};

type OptionInputEvent =
	| React.MouseEvent
	| React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>;

const TwilioEditForm: FC<IConnectorEditDialog> = ({ gridSpacing, formik }) => {
	const { setFieldValue } = formik;

	const toggleOption = (e: OptionInputEvent, name: string, value: string) => {
		e.preventDefault();
		e.stopPropagation();
		setFieldValue(name, value);
	};

	return (
		<Grid container>
			<Grid item xs={12} mb={gridSpacing}>
				<TextFieldValidation
					autoFocus
					id={FORM_KEYS.serviceName}
					type="text"
					formik={formik}
					label={intl.tr(locale.serviceName)}
					required
					fullWidth
				/>
			</Grid>
			<Grid item xs={12} mb={gridSpacing}>
				<TextFieldValidation
					id={FORM_KEYS.accountSid}
					type="text"
					formik={formik}
					label={intl.tr(locale.accountSid)}
					required
					fullWidth
				/>
			</Grid>
			<Grid item xs={12} mb={gridSpacing}>
				<FormControl style={{ width: '100%' }}>
					<FormLabel>
						{intl.tr('connectors.edit.dialog.form.field.label.credentials')}
					</FormLabel>
					<RadioGroup
						onChange={formik.handleChange}
						value={formik.values.selectedAuthProp}
					>
						<Grid
							container
							mt={2}
							display="flex"
							alignContent="flex-start"
							alignItems="center"
						>
							<Grid item xs={12}>
								<FormControlLabel
									value={FORM_KEYS.methodAuthToken}
									control={<Radio />}
									label={intl.tr(
										'connectors.edit.dialog.form.field.method.authToken'
									)}
									onClick={(e) =>
										toggleOption(
											e,
											FORM_KEYS.selectedAuthProp,
											FORM_KEYS.methodAuthToken
										)
									}
								/>
							</Grid>
							<Grid item xs={12}>
								<FormControlLabel
									value={FORM_KEYS.methodApiSecret}
									control={<Radio />}
									label={intl.tr(
										'connectors.edit.dialog.form.field.method.apiSecret'
									)}
									onClick={(e) =>
										toggleOption(
											e,
											FORM_KEYS.selectedAuthProp,
											FORM_KEYS.methodApiSecret
										)
									}
								/>
							</Grid>
						</Grid>
					</RadioGroup>
					<Grid item xs={12} mt={2}>
						{formik.values.selectedAuthProp === FORM_KEYS.methodAuthToken && (
							<Grid container>
								<Grid item xs={12}>
									<TextFieldValidation
										id={FORM_KEYS.authToken}
										type="password"
										formik={formik}
										label={intl.tr(
											'connectors.edit.dialog.form.field.method.authToken'
										)}
										inputProps={{
											'data-testid': CONNECTORS_FORM_KEYS.authToken
										}}
										required
										fullWidth
									/>
								</Grid>
							</Grid>
						)}
						{formik.values.selectedAuthProp === FORM_KEYS.methodApiSecret && (
							<Grid container>
								<Grid item xs={12} mb={gridSpacing}>
									<TextFieldValidation
										id={FORM_KEYS.apiKey}
										type="text"
										formik={formik}
										label={intl.tr(locale.apiKey)}
										required={
											formik.values.selectedAuthProp ===
											FORM_KEYS.methodApiSecret
										}
										inputProps={{
											'data-testid': CONNECTORS_FORM_KEYS.apiKey
										}}
										fullWidth
									/>
								</Grid>
								<Grid item xs={12}>
									<TextFieldValidation
										id={FORM_KEYS.apiSecret}
										type="password"
										formik={formik}
										label={intl.tr(locale.apiSecret)}
										required={
											formik.values.selectedAuthProp ===
											FORM_KEYS.methodApiSecret
										}
										inputProps={{
											'data-testid': CONNECTORS_FORM_KEYS.apiSecret
										}}
										fullWidth
									/>
								</Grid>
							</Grid>
						)}
					</Grid>
				</FormControl>
			</Grid>
			<Grid item xs={12} style={{ width: '100%' }}>
				<FormControl style={{ width: '100%' }}>
					<FormLabel>
						{intl.tr('connectors.edit.dialog.form.field.label.from')}
					</FormLabel>
					<RadioGroup
						onChange={formik.handleChange}
						value={formik.values.selectedProp}
					>
						<Grid
							item
							mt={2}
							display="flex"
							alignContent="flex-start"
							alignItems="center"
						>
							<Grid item>
								<FormControlLabel
									value={FORM_KEYS.fromPhone}
									control={<Radio />}
									label=""
									onClick={(e) =>
										toggleOption(e, FORM_KEYS.selectedProp, FORM_KEYS.fromPhone)
									}
								/>
							</Grid>
							<Grid item sx={{ flexGrow: 1 }}>
								<TextFieldValidation
									id={FORM_KEYS.fromPhone}
									type="tel"
									formik={formik}
									value={formik.values.fromPhone}
									label={intl.tr(locale.number)}
									required={formik.values.selectedProp === FORM_KEYS.fromPhone}
									fullWidth
									inputProps={{ 'data-testid': CONNECTORS_FORM_KEYS.fromPhone }}
									onClick={(e) =>
										toggleOption(e, FORM_KEYS.selectedProp, FORM_KEYS.fromPhone)
									}
								/>
							</Grid>
						</Grid>
						<Grid
							item
							mt={2}
							display="flex"
							alignContent="flex-start"
							alignItems="center"
						>
							<Grid item>
								<FormControlLabel
									value={FORM_KEYS.messagingServiceSid}
									control={<Radio />}
									label=""
									onClick={(e) =>
										toggleOption(
											e,
											FORM_KEYS.selectedProp,
											FORM_KEYS.messagingServiceSid
										)
									}
								/>
							</Grid>
							<Grid item sx={{ flexGrow: 1 }}>
								<TextFieldValidation
									id={FORM_KEYS.messagingServiceSid}
									type="tel"
									formik={formik}
									value={formik.values.messagingServiceSid}
									label={intl.tr(locale.messagingServiceSid)}
									required={
										formik.values.selectedProp === FORM_KEYS.messagingServiceSid
									}
									fullWidth
									inputProps={{
										'data-testid': CONNECTORS_FORM_KEYS.messagingServiceSid
									}}
									onClick={(e) =>
										toggleOption(
											e,
											FORM_KEYS.selectedProp,
											FORM_KEYS.messagingServiceSid
										)
									}
								/>
							</Grid>
						</Grid>
					</RadioGroup>
				</FormControl>
			</Grid>
		</Grid>
	);
};

export default TwilioEditForm;
